/**
 * Date utility functions for validation and formatting
 */

/**
 * Get today's date as a string in YYYY-MM-DD format
 * @returns Today's date in ISO date format (YYYY-MM-DD)
 */
export const getTodayDateString = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Check if a date string is in the future
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns true if the date is in the future, false otherwise
 */
export const isDateInFuture = (dateString: string): boolean => {
  const today = getTodayDateString();
  return dateString > today; // String comparison works for YYYY-MM-DD format
};

/**
 * Convert a date string to ISO format at noon UTC to avoid timezone issues
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns ISO string at noon UTC (e.g., "2025-01-26T12:00:00.000Z")
 */
export const dateToISOAtNoon = (dateString: string): string => {
  return dateString ? `${dateString}T12:00:00.000Z` : '';
};

/**
 * Extract date portion from ISO datetime string
 * @param isoString - ISO datetime string (e.g., "2025-01-26T12:00:00.000Z")
 * @returns Date string in YYYY-MM-DD format
 */
export const extractDateFromISO = (isoString: string): string => {
  return isoString ? isoString.split('T')[0] : '';
};

/**
 * Format a YYYY-MM-DD date string for display (e.g., "January 15, 2024").
 * Uses the local Date constructor to avoid UTC-midnight parsing, which causes
 * off-by-one-day errors in timezones behind UTC.
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Formatted date string (e.g., "January 15, 2024")
 */
export const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric'
  });
};

/**
 * Check if a jobsite visit is editable based on the 60-day rule
 * @param visitDate - Visit date as ISO string or Date object
 * @returns true if editable (within 60 days), false if locked
 */
export const isVisitEditable = (visitDate: string | Date | null): boolean => {
  if (!visitDate) {
    return true; // Edge case: no visit date means editable
  }

  const now = new Date();
  const visit = new Date(visitDate);

  // Calculate difference in calendar days
  const diffTime = now.getTime() - visit.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays <= 60;
};
